import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule, inject, provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router, RouterModule, TitleStrategy } from '@angular/router';
import { provideSvgIconsConfig } from '@ngneat/svg-icon';
import * as Sentry from '@sentry/angular';

import { ApiModule, Configuration } from '@api-open';
import { AuditApiModule, Configuration as ConfigurationAudit } from '@api-open-audit';
import { Configuration as ConfigurationShowback, ShowbackApiModule } from '@api-open-showback';
import { ErrorInterceptor } from '@tk/app/interceptors';
import { ToastManagerComponent } from '@tk/app/toast-manager';
import { TokenInterceptor } from '@tk/features/auth';
import { CustomizationService } from '@tk/shared/lib';
import { CONFIG } from 'src/config';
import { PageTitleStrategy } from 'src/page-title-strategy.service';

import { AppComponent } from './app.component';
import { alertsIcons } from './icons/alerts';
import { arrowsIcons } from './icons/arrows';
import { chartsIcons } from './icons/charts';
import { communicationIcons } from './icons/communication';
import { developmentIcons } from './icons/development';
import { editorIcons } from './icons/editor';
import { educationIcons } from './icons/education';
import { filesIcons } from './icons/files';
import { financeIcons } from './icons/finance';
import { generalIcons } from './icons/general';
import { imagesIcons } from './icons/images';
import { layoutIcons } from './icons/layout';
import { logoIcons } from './icons/logo';
import { mapsIcons } from './icons/maps';
import { mediaIcons } from './icons/media';
import { oldIcons } from './icons/old';
import { securityIcons } from './icons/security';
import { shapesIcons } from './icons/shapes';
import { taikunIcons } from './icons/taikun';
import { timeIcons } from './icons/time';
import { usersIcons } from './icons/users';
import { appRoutes } from './routes-lazy';

const icons = [
  ...alertsIcons,
  ...arrowsIcons,
  ...chartsIcons,
  ...communicationIcons,
  ...developmentIcons,
  ...editorIcons,
  ...educationIcons,
  ...filesIcons,
  ...financeIcons,
  ...generalIcons,
  ...imagesIcons,
  ...layoutIcons,
  ...logoIcons,
  ...mapsIcons,
  ...oldIcons,
  ...securityIcons,
  ...shapesIcons,
  ...taikunIcons,
  ...timeIcons,
  ...usersIcons,
  ...mediaIcons,
];

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    ApiModule.forRoot(
      () =>
        new Configuration({
          basePath: CONFIG.apiUrlGenerated,
        }),
    ),
    ShowbackApiModule.forRoot(
      () =>
        new ConfigurationShowback({
          basePath: CONFIG.apiUrlGenerated,
        }),
    ),
    AuditApiModule.forRoot(
      () =>
        new ConfigurationAudit({
          basePath: CONFIG.apiUrlGenerated,
        }),
    ),
    BrowserAnimationsModule,
    RouterModule.forRoot(appRoutes, { paramsInheritanceStrategy: 'always', bindToComponentInputs: true }),
    ToastManagerComponent,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true,
    },
    { provide: TitleStrategy, useClass: PageTitleStrategy },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    provideAppInitializer(() => {
      const initializerFn = () => {
        inject(Sentry.TraceService);
        return;
      };
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const initializerFn = ((customizationService: CustomizationService) => (): void => {
        customizationService.partnerInfo.value();
      })(inject(CustomizationService));
      return initializerFn();
    }),
    provideSvgIconsConfig({
      icons,
      sizes: {
        md: '1.125rem',
      },
      defaultSize: 'md',
    }),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
